import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

const formatMoney = (cents, formatString) => {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  formatString = formatString || window.theme?.moneyFormat || "${{amount}}";

  function defaultOption(opt, def) {
    return typeof opt === "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    let parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  switch (match[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
};

class CartBundle extends Component {
  productData = null;
  productList = [];
  currentMatchedCartItems = [];
  sellingPlan = null;
  _isMatched = false;
  _clickBound = false;
  _classObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.moneyFormat = this.getAttribute("data-money-format");
    this.initData();
    this.renderPrices();

    // Observe class changes: if morphDOM overwrites the class and removes `is-visible`
    // while we are in matched state, restore `is-visible` synchronously.
    if (!this._classObserver) {
      this._classObserver = new MutationObserver(() => {
        if (this._isMatched && !this.classList.contains("is-visible")) {
          this.classList.add("is-visible");
        }
      });
      this._classObserver.observe(this, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    if (!this._clickBound) {
      this.addEventListener("click", (e) => {
        const btn = e.target.closest('[data-component="submit-btn"]');
        if (btn) {
          e.preventDefault();
          this.handleSubmit(btn);
        }
      });
      this._clickBound = true;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    this.checkCart();
  }

  /**
   * Called by morph.js onAfterUpdate whenever the component is morphed.
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.initData();
    this.renderPrices();
    if (this._isMatched) {
      this.classList.add("is-visible");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this._classObserver?.disconnect();
    this._classObserver = null;
  }

  initData() {
    const productScript = this.querySelector("[data-cart-bundle-product]");
    const listScript = this.querySelector("[data-cart-bundle-product-list]");

    if (productScript) {
      try {
        this.productData = JSON.parse(productScript.textContent);
      } catch (e) {
        console.error("Failed to parse cart bundle product data", e);
      }
    }

    if (listScript) {
      try {
        this.productList = JSON.parse(listScript.textContent);
      } catch (e) {
        console.error("Failed to parse cart bundle product list data", e);
      }
    }

    if (
      this.productData &&
      this.productData.selling_plan_groups &&
      this.productData.selling_plan_groups.length > 0
    ) {
      const firstGroup = this.productData.selling_plan_groups[0];
      if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
        this.sellingPlan = firstGroup.selling_plans[0];
      }
    }
  }

  renderPrices() {
    if (!this.productData) return;

    const priceElement = this.querySelector('[data-component="price"]');
    const comparedPriceElement = this.querySelector(
      '[data-component="compared-price"]',
    );
    const priceSuffixElement = this.querySelector(
      '[data-component="price-suffix"]',
    );

    let currentPrice = this.productData.price;
    let comparedPrice = null;
    let newPrice = currentPrice;

    if (this.sellingPlan) {
      comparedPrice = currentPrice;
      const adjustment = this.sellingPlan.price_adjustments[0];

      if (adjustment) {
        if (adjustment.value_type === "percentage") {
          newPrice = currentPrice - currentPrice * (adjustment.value / 100);
        } else if (adjustment.value_type === "fixed_amount") {
          newPrice = currentPrice - adjustment.value;
        } else if (adjustment.value_type === "price") {
          newPrice = adjustment.value;
        }
      }
    }

    if (priceElement) {
      priceElement.innerHTML = formatMoney(newPrice, this.moneyFormat);
    }

    if (comparedPriceElement) {
      if (comparedPrice) {
        comparedPriceElement.innerHTML = formatMoney(
          comparedPrice,
          this.moneyFormat,
        );
        comparedPriceElement.style.display = "block";
      } else {
        comparedPriceElement.style.display = "none";
      }
    }

    if (!this.sellingPlan && priceSuffixElement) {
      priceSuffixElement.style.display = "none";
    }
  }

  handleCartUpdate = (event) => {
    const resource = event?.detail?.resource;
    if (resource && Array.isArray(resource.items)) {
      this.processCartItems(resource.items);
    } else {
      this.checkCart();
    }
  };

  checkCart() {
    fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((cart) => {
        this.initData();
        this.renderPrices();
        this.processCartItems(cart.items);
      })
      .catch((e) => console.error("Error fetching cart", e));
  }

  processCartItems(items) {
    if (!this.productList || this.productList.length === 0) {
      this._isMatched = false;
      this.classList.remove("is-visible");
      return;
    }

    const mainProductId = this.productData?.id
      ? String(this.productData.id)
      : null;
    const isMainProductInCart = (items || []).some(
      (item) => item && String(item.product_id) === mainProductId,
    );

    // If the main bundle is already in the cart, hide the component
    if (isMainProductInCart) {
      this._isMatched = false;
      this.classList.remove("is-visible");
      this.currentMatchedCartItems = [];
      return;
    }

    const listProductIds = this.productList
      .filter((p) => p && p.id)
      .map((p) => String(p.id));

    this.currentMatchedCartItems = (items || []).filter((item) => {
      if (!item || !item.product_id) return false;
      return listProductIds.includes(String(item.product_id));
    });

    if (this.currentMatchedCartItems.length > 0) {
      this._isMatched = true;
      this.classList.add("is-visible");
    } else {
      this._isMatched = false;
      this.classList.remove("is-visible");
    }
  }

  handleSubmit(btn) {
    btn.classList.add("loading");
    btn.setAttribute("disabled", "disabled");

    fetch(window.Shopify.routes.root + "cart.js")
      .then((res) => res.json())
      .then((cart) => {
        this.initData();

        if (
          !this.productList ||
          this.productList.length === 0 ||
          !this.productData
        ) {
          this.resetBtn(btn);
          return;
        }

        const listProductIds = this.productList
          .filter((p) => p && p.id)
          .map((p) => String(p.id));

        this.currentMatchedCartItems = cart.items.filter((item) => {
          if (!item || !item.product_id) return false;
          return listProductIds.includes(String(item.product_id));
        });

        if (this.currentMatchedCartItems.length === 0) {
          this.resetBtn(btn);
          return;
        }

        this.performCartUpdate(btn);
      })
      .catch((error) => {
        console.error("Error fetching cart before submit", error);
        this.resetBtn(btn);
      });
  }

  performCartUpdate(btn) {
    const updates = {};
    this.currentMatchedCartItems.forEach((item) => {
      updates[item.key] = 0;
    });

    const variantId =
      this.productData.variants && this.productData.variants.length > 0
        ? this.productData.variants[0].id
        : null;

    if (!variantId) {
      console.error("No variant found for the bundle product");
      this.resetBtn(btn);
      return;
    }

    // Step 1: Remove matched products
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then(() => {
        // Step 2: Add main bundle product
        const addData = {
          items: [{ id: variantId, quantity: 1 }],
        };

        if (this.sellingPlan) {
          addData.items[0].selling_plan = this.sellingPlan.id;
        }

        return fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(addData),
        });
      })
      .then((response) => response.json())
      .then(() => {
        // Step 3: Fetch full cart then dispatch update
        return fetch(window.Shopify.routes.root + "cart.js");
      })
      .then((response) => response.json())
      .then((fullCart) => {
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              resource: fullCart,
              data: {},
            },
          }),
        );
      })
      .catch((error) => {
        console.error("Error updating cart", error);
      })
      .finally(() => {
        this.resetBtn(btn);
      });
  }

  resetBtn(btn) {
    if (btn) {
      btn.classList.remove("loading");
      btn.removeAttribute("disabled");
    }
  }
}

if (!customElements.get("cart-bundle")) {
  customElements.define("cart-bundle", CartBundle);
}
